import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { white } from '@zocdoc/branding/colors';

import { Link } from '../../link';
import { FontFamily } from '../../typography';

import {
    AppStoreLink,
    StyledGooglePlayLink,
    StyledMadeInNYLink,
} from './IconLinks';

const LinkGroup = ({ children, name, ...props }) => (
    <section {...props}>
        {name ? (<StyledLinkGroupHeader>{name}</StyledLinkGroupHeader>) : null}

        <ul>
            {React.Children.map(children, (child, index) => (
                <StyledLinkWrapper key={index}>
                    {child}
                </StyledLinkWrapper>
            ))}
        </ul>
    </section>
);

export const StyledLinkGroup = styled(LinkGroup)`
    font: 14px/1 ${FontFamily.SharpSansSemibold};
`;

const StyledLinkGroupHeader = styled.h3`
    color: ${p => p.theme.footer.seo.textColor};
    font: 16px/1.3 ${FontFamily.SharpSansBold};
    letter-spacing: 0.1px;
    padding-top: 12px;
    padding-bottom: 12px;
`;

const StyledLinkWrapper = styled.li`
    margin: 0 0 9px;
`;

export const StyledLink = styled(Link)`
    border-bottom-color: ${p => p.theme.footer.seo.linkUnderlineColor};
    color: ${p => p.theme.footer.seo.linkColor};
    font: inherit;
    line-height: 1.5;

    &:focus,
    &:hover {
        border-bottom-color: ${p => p.theme.footer.seo.linkUnderlineColorHover};
        color: ${p => p.theme.footer.seo.linkColorHover};
    }

    &:visited {
        border-bottom-color: ${p => p.theme.footer.seo.linkUnderlineColorVisited};
        color: ${p => p.theme.footer.seo.linkColorVisited};
    }
`;

export function ZocdocLinkGroup({ onBeforeLinkClick, ...props }) {
    return (
        <StyledLinkGroup
            name="Zocdoc"
            {...props}
        >
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href="//www.zocdoc.com/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Home"
                    id="Components.Layout.Footer.SEO.Home"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href="/about/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="About"
                    id="Components.Layout.Footer.SEO.About"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href="/about/press/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Press"
                    id="Components.Layout.Footer.SEO.Press"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href="/about/careers/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Careers"
                    id="Components.Layout.Footer.SEO.Careers"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href="/about/contactus/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Contact"
                    id="Components.Layout.Footer.SEO.Contact"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href="//support.zocdoc.com/patients/s/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Help"
                    id="Components.Layout.Footer.SEO.Help"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href="//www.zocdoc.com/about/blog/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Blogs"
                    id="Components.Layout.Footer.SEO.Blogs"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href=" //thescript.zocdoc.com"
                target="_blank"
            >
                The Script
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Zocdoc"
                href="/answers/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Health Q&A"
                    id="Components.Layout.Footer.SEO.DoctorBlog"
                />
            </StyledLink>
        </StyledLinkGroup>
    );
}

export function BrowseLinkGroup({ onBeforeLinkClick, ...props }) {
    return (
        <StyledLinkGroup
            name={
                (
                    <FormattedMessage
                        defaultMessage="Look up your doctor"
                        id="Layout.Footer.SEO.FindYourDoctor"
                    />
                )
            }
            {...props}
        >
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="LookUpYourDoctor"
                href="/directory"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Doctor name"
                    id="Components.Layout.Footer.SEO.DoctorName"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="LookUpYourDoctor"
                href="/practicedirectory/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Practice name"
                    id="Components.Layout.Footer.SEO.PracticeDirectory"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="LookUpYourDoctor"
                href="/hospital-directory/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Hospital name"
                    id="Components.Layout.Footer.SEO.Hospital"
                />
            </StyledLink>
        </StyledLinkGroup>
    );
}

export function SearchLinkGroup({ onBeforeLinkClick, ...props }) {
    return (
        <StyledLinkGroup
            name={
                (
                    <FormattedMessage
                        defaultMessage="Search by"
                        id="Layout.Footer.SEO.SearchZocdocMarketplace"
                    />
                )
            }
            {...props}
        >
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-test="search-by-specialty-link"
                data-title="SearchBy"
                href="/morespecialties/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Specialty"
                    id="Components.Layout.Footer.SEO.Specialty"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="SearchBy"
                href="/procedures/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Procedure"
                    id="Components.Layout.Footer.SEO.Procedure"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="SearchBy"
                href="/languages/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Language"
                    id="Components.Layout.Footer.SEO.Language"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="SearchBy"
                href="/locations/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Location"
                    id="Components.Layout.Footer.SEO.Location"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="SearchBy"
                href="/insurances/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Insurance"
                    id="Components.Layout.Footer.SEO.Insurance"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="SearchBy"
                href="/doctor-reviews/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Reviews"
                    id="Components.Layout.Footer.SEO.Reviews"
                />
            </StyledLink>
        </StyledLinkGroup>
    );
}

export function SEOLinkGroup({ onBeforeLinkClick, ...props }) {
    return (
        <StyledLinkGroup {...props}>
            <BrowseLinkGroup onBeforeLinkClick={onBeforeLinkClick} />
            <SearchLinkGroup onBeforeLinkClick={onBeforeLinkClick} />
        </StyledLinkGroup>
    );
}

export function CitiesLinkGroup({ onBeforeLinkClick, ...props }) {
    return (
        <StyledLinkGroup
            name={
                (
                    <FormattedMessage
                        defaultMessage="Cities"
                        id="Layout.Footer.SEO.Cities"
                    />
                )
            }
            {...props}
        >
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/atlanta-218712pm"
                target="_blank"
            >
                Atlanta
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/chicago-210499pm"
                target="_blank"
            >
                Chicago
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/denver-228319pm"
                target="_blank"
            >
                Denver
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/houston-215108pm"
                target="_blank"
            >
                Houston
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/new-york-46063pm"
                target="_blank"
            >
                New York
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/philadelphia-216871pm"
                target="_blank"
            >
                Philadelphia
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/phoenix-220419pm"
                target="_blank"
            >
                Phoenix
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/san-antonio-225141pm"
                target="_blank"
            >
                San Antonio
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Cities"
                href="/primary-care-doctors/washington-34993pm"
                target="_blank"
            >
                Washington DC
            </StyledLink>
        </StyledLinkGroup>
    );
}

export function SpecialtiesLinkGroup({ onBeforeLinkClick, ...props }) {
    return (
        <StyledLinkGroup
            name={
                (
                    <FormattedMessage
                        defaultMessage="Specialties"
                        id="Layout.Footer.SEO.Specialties"
                    />
                )
            }
            {...props}
        >
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-test="specialties-chiropractors-link"
                data-title="Specialties"
                href="/chiropractors"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Chiropractors"
                    id="Components.Layout.Footer.SEO.Chiropractors"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Specialties"
                href="/dentists"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Dentists"
                    id="Components.Layout.Footer.SEO.Dentists"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Specialties"
                href="/dermatologists"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Dermatologists"
                    id="Components.Layout.Footer.SEO.Dermatologists"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Specialties"
                href="/eye-doctors"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Eye Doctors"
                    id="Components.Layout.Footer.SEO.EyeDoctors"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Specialties"
                href="/obgyns"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Gynecologists"
                    id="Components.Layout.Footer.SEO.Gynecologists"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Specialties"
                href="/primary-care-doctors"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Primary care doctors"
                    id="Components.Layout.Footer.SEO.PrimaryCareDoctors"
                />
            </StyledLink>
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-title="Specialties"
                href="/psychiatrists"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="Psychiatrists"
                    id="Components.Layout.Footer.SEO.Psychiatrists"
                />
            </StyledLink>
        </StyledLinkGroup>
    );
}

export function CTALinkGroup({ onBeforeLinkClick, ...props }) {
    return (
        <StyledLinkGroup
            name={
                (
                    <div>
                        <FormattedMessage
                            defaultMessage="Are you a top"
                            id="Layout.Footer.SEO.AreYouATop"
                        />
                        <br />
                        <FormattedMessage
                            defaultMessage="doctor or dentist?"
                            id="Layout.Footer.SEO.DoctorOrDentist"
                        />
                    </div>
                )
            }
            {...props}
        >
            <StyledLink
                beforeClick={onBeforeLinkClick}
                data-test="AreYouADoctorOrDentist"
                data-title="AreYouADoctorOrDentist"
                href="/join/whyjoin/"
                target="_blank"
            >
                <FormattedMessage
                    defaultMessage="List your practice on Zocdoc"
                    id="Components.Layout.Footer.SEO.JoinZocdoc"
                />
            </StyledLink>
            <StyledLinkGroup
                name={
                    (
                        <FormattedMessage
                            defaultMessage="Zocdoc for health systems"
                            id="Layout.Footer.SEO.ZocdocForHealthSystems"
                        />
                    )
                }
            >
                <StyledLink
                    beforeClick={onBeforeLinkClick}
                    data-test="ZocdocForHealthSystems"
                    data-title="ZocdocForHealthSystems"
                    href="/about/healthsystems/"
                    target="_blank"
                    title="Learn about Zocdoc's value proposition for health systems"
                >
                    <FormattedMessage
                        defaultMessage="Learn more"
                        id="Components.Layout.Footer.SEO.LearnMore"
                    />
                </StyledLink>
            </StyledLinkGroup>
        </StyledLinkGroup>
    );
}

export function BadgeLinkGroup({ onBeforeLinkClick, ...props }) {
    return (
        <StyledLinkGroup
            data-title="Badges"
            {...props}
        >
            <AppStoreLink
                height={36}
                onBeforeLinkClick={onBeforeLinkClick}
                title="Download the Zocdoc app from the Apple App Store"
            />
            <StyledGooglePlayLink
                height={36}
                onBeforeLinkClick={onBeforeLinkClick}
                title="Download the Zocdoc app from the Google Play Store"
            />
            <StyledMadeInNYLink
                color={white}
                height={40}
                onBeforeLinkClick={onBeforeLinkClick}
                title="View Zocdoc's profile on the Digital NYC startup directory"
            />
        </StyledLinkGroup>
    );
}
